import {UntypedFormControl, ValidatorFn} from '@angular/forms';
import {TemplateService} from 'app/fragment/services/template.service';
import {Bucketer} from '../../common/bucketer';
import {ErrorMessageTypes} from './error-messages';
import {
  EmptyBucketError,
  GridRefError,
  InvalidOptionError,
  MaxLengthError,
  NumericError,
  WhitespaceError,
  YearError,
} from './types';

/**
 * Static class defining custom ValidatorFn's for DAS 3.0.
 */
export class FormValidator {
  private static readonly NUMERIC_REGEX: RegExp = /^[0-9]*$/;

  private static readonly GRID_REF_REGEX_1: RegExp = /^[0-9]{6}, [0-9]{6}$/;
  private static readonly GRID_REF_REGEX_2: RegExp = /^[0-9]{6},[0-9]{6}$/;
  private static readonly GRID_REF_REGEX_3: RegExp = /^[A-Z]{2} [0-9]{5} [0-9]{5}$/;
  private static readonly GRID_REF_REGEX_4: RegExp = /^[A-Z]{2} [0-9]{3} [0-9]{3}$/;

  private static readonly WHITE_SPACE_REGEX: RegExp = /^\s*$/;

  /**
   * Create a ValidatorFn which validates the form control by checking it doesn't
   * contain any match for FormValidators.NUMERIC_REGEX.
   *
   * @param control {FormControl}    The host form control
   * @returns       {NumericError}   A form numeric error or null
   */
  public static numeric(control: UntypedFormControl): NumericError {
    const value: string = control.value;
    return value && !FormValidator.isNumeric(value) ? {[ErrorMessageTypes.NUMERIC]: true} : null;
  }

  /**
   * Create a ValidatorFn which validates the form control by checking it doesn't contain
   * any match for any of the FormValidator.GRID_REF_REGEX* regular expressions.
   *
   * @param control {FormControl}    The host form control
   * @returns       {GridRefError}   A form grid ref error or null
   */
  public static gridRef(control: UntypedFormControl): GridRefError {
    const gridRef: string = control.value;
    return gridRef && !FormValidator.isValidGridRef(gridRef) ? {gridRef: true} : null;
  }

  private static isValidGridRef(gridRef: string): boolean {
    return (
      FormValidator.GRID_REF_REGEX_1.test(gridRef) ||
      FormValidator.GRID_REF_REGEX_2.test(gridRef) ||
      FormValidator.GRID_REF_REGEX_3.test(gridRef) ||
      FormValidator.GRID_REF_REGEX_4.test(gridRef)
    );
  }

  public static whitespace(control: UntypedFormControl): WhitespaceError {
    const value: string = control.value;
    return value && FormValidator.WHITE_SPACE_REGEX.test(value) ? {[ErrorMessageTypes.WHITESPACE]: true} : null;
  }

  public static year(control: UntypedFormControl): YearError {
    const value: string = control.value;
    return value && !(value.length === 4 && FormValidator.isNumeric(value)) ? {[ErrorMessageTypes.YEAR]: true} : null;
  }

  public static validTemplate(control: UntypedFormControl): InvalidOptionError {
    const value: string = control.value;
    return value && !TemplateService.isValidTemplate(value) ? {[ErrorMessageTypes.INVALID_OPTION]: true} : null;
  }

  public static maxCharacterLength(maxLength: number): ValidatorFn {
    return (control: UntypedFormControl): MaxLengthError => {
      const value: string = control.value;
      return value && value.length > maxLength ? {[ErrorMessageTypes.MAX_LENGTH]: true} : null;
    };
  }

  public static emptyBucketValidatorFactory<T>(bucketer: Bucketer<T>): ValidatorFn {
    if (!bucketer) {
      return (control: UntypedFormControl): EmptyBucketError => {
        return {[ErrorMessageTypes.EMPTY_BUCKET]: true};
      };
    }

    return (control: UntypedFormControl): EmptyBucketError => {
      return bucketer.getSelected().length ? null : {[ErrorMessageTypes.EMPTY_BUCKET]: true};
    };
  }

  private static isNumeric(value: string): boolean {
    return FormValidator.NUMERIC_REGEX.test(value);
  }

  private constructor() {
    // Prevent external instantiation.
  }
}
